import React from 'react';
import { Box, Flex, Link, Text } from 'rebass';
import { useLocation } from 'react-router-dom';
import AuthPageSkeleton from '../auth/components/AuthPageSkeleton';
import AuthForm from '../auth/components/AuthForm';
import Button from '@/components/Button/Button';
import Spinner from '@/components/Spinner/Spinner';
import theme from '@/theme';
import {
  approveAuthorization,
  denyAuthorization,
  describeAuthorization,
  OAuthAuthorizationDetails,
  OAuthLoginRequiredError,
} from '@/services/oAuth';

/** Send the user to log in, then back to this consent page. */
const goToLogin = () => {
  localStorage.removeItem('auth-token');
  const here = `${window.location.pathname}${window.location.search}`;
  window.location.replace(`/login?redirect=${encodeURIComponent(here)}`);
};

/**
 * @component OAuth consent page for MCP clients (Claude, Cursor, ...).
 * The API validates the request and redirects here; the user approves with
 * their normal EcosConnect login, and we send them back to the client.
 */
const OAuthAuthorize: React.FC = () => {
  const { search } = useLocation();
  const [details, setDetails] = React.useState<OAuthAuthorizationDetails>();
  const [errorMessage, setErrorMessage] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleError = (err: unknown) => {
    if (err instanceof OAuthLoginRequiredError) {
      goToLogin();
      return;
    }
    setErrorMessage((err as Error).message);
    setIsSubmitting(false);
  };

  React.useEffect(() => {
    describeAuthorization(search).then(setDetails).catch(handleError);
  }, [search]);

  const decide = async (approved: boolean) => {
    setIsSubmitting(true);
    setErrorMessage('');
    try {
      const { redirectUrl } = approved
        ? await approveAuthorization(search)
        : await denyAuthorization(search);
      window.location.replace(redirectUrl);
    } catch (err) {
      handleError(err);
    }
  };

  const clientName = details?.clientName || 'An application';

  return (
    <AuthPageSkeleton>
      <AuthForm
        onSubmit={(e: React.FormEvent) => {
          e.preventDefault();
          decide(true);
        }}
      >
        {!details && !errorMessage && (
          <Flex justifyContent="center" mt={7}>
            <Spinner
              size={48}
              color={theme.colors.primary}
              backgroundColor="#eee"
            />
          </Flex>
        )}

        {details && (
          <Box mt={5}>
            <Text as="h2" fontSize={4} fontWeight="bold">
              Connect {clientName} to EcosConnect
            </Text>
            <Text mt={3}>
              <strong>{clientName}</strong> is requesting access to your EcosConnect{' '}
              <strong>{details.server.label}</strong> account
              {details.company.name ? (
                <>
                  {' '}
                  for <strong>{details.company.name}</strong>
                </>
              ) : null}
              .
            </Text>

            {!details.denialReason && (
              <Text mt={3} color="gray">
                It will be able to read and change the data you can access in
                EcosConnect, with your permissions, until you disconnect it.
              </Text>
            )}

            <Box
              mt={4}
              p={3}
              sx={{ border: '1px solid', borderColor: 'gray', borderRadius: 4 }}
            >
              <Text fontSize={1} color="gray">
                After you respond you will be sent to
              </Text>
              <Text fontWeight="bold" sx={{ wordBreak: 'break-all' }}>
                {details.redirectHost}
              </Text>
              <Text fontSize={1} color="gray" mt={1}>
                Only continue if you started this connection yourself.
              </Text>
            </Box>

            <Text mt={4} fontSize={1} color="gray">
              Signed in as {details.user.email}.{' '}
              <Link
                href="#"
                color={theme.colors.secondary}
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  goToLogin();
                }}
              >
                Use a different account
              </Link>
            </Text>
          </Box>
        )}

        {(errorMessage || details?.denialReason) && (
          <Text variant="subheading" mt={4} sx={{ color: '#D9534F' }}>
            {errorMessage || details?.denialReason}
          </Text>
        )}

        {details && (
          <Flex mt={5} sx={{ gap: 3 }} flexDirection="column">
            {!details.denialReason && (
              <Button
                width="100%"
                justifyContent="center"
                paddingY={3}
                type="submit"
                variant="primary"
                disabled={isSubmitting}
              >
                Approve
              </Button>
            )}
            <Button
              width="100%"
              justifyContent="center"
              paddingY={3}
              type="button"
              variant="secondary"
              disabled={isSubmitting}
              onClick={() => decide(false)}
            >
              Cancel
            </Button>
          </Flex>
        )}
      </AuthForm>
    </AuthPageSkeleton>
  );
};

export default OAuthAuthorize;
